// Build it (T3–T11): the kit check, then step by step to sign-off, with holds,
// problems, readings and the shift handover. It resumes wherever the build
// is, on any tablet, for any technician: the state lives in the build.
import { useState } from 'react';
import { Link, useParams } from 'react-router';

import type { BuildDetailResponse } from '../../api/generated/BuildDetailResponse';
import { useBuild, useBuildActions } from '../../api/queries';
import { canBuild, useSession } from '../../auth/session';
import {
    Badge,
    Button,
    ErrorPanel,
    Loading,
    PageHeader,
    ProgressBar,
    SignInHint,
    StateBadge,
} from '../../design/components';
import { formatDate, formatDateTime, titleOf } from '../../design/format';
import { DocumentsDrawer } from '../documents/DocumentsDrawer';
import { waitingText } from '../queue/BuildCard';
import { HandoverPanel } from './HandoverPanel';
import { HoldDialog } from './HoldDialog';
import { HoldPanel } from './HoldPanel';
import { KitCheck } from './KitCheck';
import type { PartOption, ProblemDraft } from './ProblemDialog';
import { ProblemDialog } from './ProblemDialog';
import { ReadingForm } from './ReadingForm';
import { StepActions, StepView } from './StepView';

export function BuildPage() {
    const { buildId = '' } = useParams();
    const build = useBuild(buildId);
    if (build.isPending) return <Loading what="the build" />;
    if (build.isError) return <ErrorPanel error={build.error} title="The build couldn’t be loaded." />;
    return <BuildView build={build.data} />;
}

function partsOf(build: BuildDetailResponse, stepNumber: number | null): PartOption[] {
    const step = build.steps.find((candidate) => candidate.number === stepNumber);
    const parts = step
        ? step.parts.map((part) => ({
              partNumber: part.activePartNumber,
              label: `${part.activePartNumber} ${part.name ?? ''}`,
          }))
        : build.kit.lines.map((line) => ({
              partNumber: line.partNumber,
              label: `${line.partNumber} ${line.name ?? ''}`,
          }));
    const assembly = {
        partNumber: build.summary.assemblyPartNumber,
        label: `${build.summary.assemblyPartNumber} (the assembly)`,
    };
    return [...parts, assembly];
}

function BuildView({ build }: { build: BuildDetailResponse }) {
    const session = useSession();
    const actions = useBuildActions(build.summary.buildId);
    const canAct = canBuild(session);
    const { summary } = build;
    const unit = build.units.at(-1);
    const [viewing, setViewing] = useState<number | null>(null);
    const shown = viewing ?? build.nextStep ?? build.steps.at(-1)?.number ?? null;
    const step = build.steps.find((candidate) => candidate.number === shown);
    const [problemOpen, setProblemOpen] = useState(false);
    const [holdOpen, setHoldOpen] = useState(false);
    const [drawings, setDrawings] = useState<string | null>(null);
    const waiting = waitingText(summary);
    const working = summary.state === 'InProgress';
    const allDone = working && build.nextStep === null;

    const raise = (draft: ProblemDraft) => {
        actions.raiseProblem.mutate(
            {
                partNumber: draft.partNumber,
                note: draft.note,
                stepNumber: working ? shown : null,
                photoBase64: draft.photo?.base64 ?? null,
                photoContentType: draft.photo?.contentType ?? null,
                stopBuild: draft.stop,
            },
            {
                onSuccess: () => {
                    setProblemOpen(false);
                },
            },
        );
    };

    return (
        <div className="sf-stack">
            {!session.signedIn && !session.loading ? <SignInHint onSignIn={session.signIn} /> : null}
            <PageHeader
                eyebrow={`Shop floor · ${summary.station}`}
                title={titleOf(summary.assemblyPartNumber, summary.assemblyName)}
                description={
                    <span className="sf-row">
                        <span>At {summary.station}</span>
                        <span>× {summary.buildQuantity}</span>
                        <span>Need by {formatDate(summary.needBy)}</span>
                        {summary.progress.units > 1 && working ? (
                            <Badge tone="neutral">
                                Unit {summary.progress.unit} of {summary.progress.units}
                            </Badge>
                        ) : null}
                    </span>
                }
                actions={
                    <>
                        <StateBadge state={summary.state} />
                        <Button
                            onClick={() => {
                                setDrawings(build.assemblyItemId);
                            }}
                        >
                            Drawings
                        </Button>
                    </>
                }
            />
            {working ||
            summary.state === 'Built' ||
            waiting ||
            build.handovers[0] ||
            build.kitReason ||
            build.engineeringChangeNote ? (
                <section className="sf-card sf-stack" aria-label="Where this build stands">
                    {working || summary.state === 'Built' ? (
                        <ProgressBar
                            done={summary.progress.stepsDone}
                            total={summary.progress.stepsTotal}
                            label="Steps done for this unit"
                        />
                    ) : null}
                    {waiting ? <div className="sf-note sf-note--info">{waiting}</div> : null}
                    {build.handovers[0] ? (
                        <div className="sf-note sf-note--info" role="note" aria-label="Latest handover">
                            <strong>Handover from {build.handovers[0].left.displayName}:</strong>{' '}
                            {build.handovers[0].note}
                        </div>
                    ) : null}
                    {build.kitReason ? <div className="sf-muted">Replacement kit: {build.kitReason}</div> : null}
                    {build.engineeringChangeNote ? (
                        <div className="sf-muted">Engineering’s note: {build.engineeringChangeNote}</div>
                    ) : null}
                </section>
            ) : null}

            <HoldPanel build={build} actions={actions} canAct={canAct} />

            {summary.state === 'Built' ? (
                <section className="sf-card sf-stack">
                    <h2>Signed off</h2>
                    <p>
                        By {build.signedOff?.displayName ?? '—'} on {formatDateTime(build.signedOff?.at)}. Nothing about
                        this build can change now.
                    </p>
                    <Link className="sf-button sf-button--outlined" to={`/lead/records/${summary.buildId}`}>
                        Open the as-built record
                    </Link>
                </section>
            ) : null}

            {build.started === null ? <KitCheck build={build} actions={actions} canAct={canAct} /> : null}

            {summary.state === 'Ready' && canAct ? (
                <div className="sf-row">
                    <Button
                        large
                        variant="primary"
                        disabled={build.kit.finished === null || actions.start.isPending}
                        onClick={() => {
                            actions.start.mutate();
                        }}
                    >
                        Start the build
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            setHoldOpen(true);
                        }}
                    >
                        Put on hold
                    </Button>
                    {build.kit.finished === null ? (
                        <span className="sf-muted">Finish the kit check to start.</span>
                    ) : null}
                </div>
            ) : null}
            {actions.start.isError ? <ErrorPanel error={actions.start.error} /> : null}

            {build.started !== null && step ? (
                <StepView step={step} stepCount={build.steps.length} unit={unit} onDrawings={setDrawings}>
                    {step.checks.map((check) => (
                        <ReadingForm
                            key={`${check.checkId}-${String(unit?.number ?? 0)}`}
                            check={check}
                            reading={unit?.readings.find(
                                (reading) => reading.step === step.number && reading.checkId === check.checkId,
                            )}
                            canAct={canAct && working && step.number === build.nextStep}
                            busy={actions.recordReading.isPending}
                            error={actions.recordReading.error}
                            onSave={(value, note) => {
                                actions.recordReading.mutate({
                                    step: step.number,
                                    body: { checkId: check.checkId, value, note },
                                });
                            }}
                            onReportProblem={() => {
                                setProblemOpen(true);
                            }}
                        />
                    ))}
                </StepView>
            ) : null}

            {working && canAct ? (
                <>
                    <StepActions
                        canBack={(shown ?? 1) > 1}
                        canDone={step?.number === build.nextStep}
                        doneLabel="Done"
                        busy={actions.completeStep.isPending}
                        onBack={() => {
                            setViewing(Math.max(1, (shown ?? 1) - 1));
                        }}
                        onDone={() => {
                            if (step) {
                                actions.completeStep.mutate(step.number, {
                                    onSuccess: () => {
                                        setViewing(null);
                                    },
                                });
                            }
                        }}
                        onProblem={() => {
                            setProblemOpen(true);
                        }}
                    />
                    <div className="sf-row sf-no-print">
                        {viewing !== null && viewing !== build.nextStep ? (
                            <Button
                                onClick={() => {
                                    setViewing(null);
                                }}
                            >
                                Back to the next step
                            </Button>
                        ) : null}
                        {unit?.completions.at(-1)?.step === shown ? (
                            <Button
                                onClick={() => {
                                    actions.reopenStep.mutate(shown);
                                }}
                            >
                                Reopen step {shown}
                            </Button>
                        ) : null}
                        <Button
                            onClick={() => {
                                setHoldOpen(true);
                            }}
                        >
                            Put on hold
                        </Button>
                    </div>
                </>
            ) : null}
            {actions.completeStep.isError ? (
                <ErrorPanel error={actions.completeStep.error} title="The step wasn’t marked done." />
            ) : null}

            {allDone && canAct ? (
                <section className="sf-card sf-stack" aria-labelledby="sign-off-heading">
                    <h2 id="sign-off-heading">
                        {summary.progress.units > 1
                            ? `Sign off unit ${String(summary.progress.unit)} of ${String(summary.progress.units)}`
                            : 'Sign off the build'}
                    </h2>
                    <p>Every step is done. Once the last unit is signed off, nothing can be changed.</p>
                    <Button
                        large
                        variant="primary"
                        disabled={actions.signOff.isPending}
                        onClick={() => {
                            actions.signOff.mutate();
                        }}
                    >
                        Sign off
                    </Button>
                    {actions.signOff.isError ? <ErrorPanel error={actions.signOff.error} /> : null}
                </section>
            ) : null}

            {build.handovers.length > 0 || (canAct && build.started) ? (
                <HandoverPanel
                    build={build}
                    actions={actions}
                    canAct={canAct && summary.state !== 'Built' && build.started !== null}
                />
            ) : null}

            <ProblemDialog
                open={problemOpen}
                context={
                    working && step
                        ? `Step ${String(step.number)}: ${step.title}`
                        : `Kit for ${summary.assemblyPartNumber}`
                }
                parts={partsOf(build, working ? shown : null)}
                busy={actions.raiseProblem.isPending}
                error={actions.raiseProblem.error}
                onClose={() => {
                    setProblemOpen(false);
                }}
                onSubmit={raise}
            />
            <HoldDialog
                open={holdOpen}
                busy={actions.hold.isPending}
                error={actions.hold.error}
                onClose={() => {
                    setHoldOpen(false);
                }}
                onHold={(reason, note) => {
                    actions.hold.mutate(
                        { reason, note: note.trim() === '' ? null : note },
                        {
                            onSuccess: () => {
                                setHoldOpen(false);
                            },
                        },
                    );
                }}
            />
            {drawings ? (
                <DocumentsDrawer
                    itemId={drawings}
                    buildId={summary.buildId}
                    onClose={() => {
                        setDrawings(null);
                    }}
                />
            ) : null}
        </div>
    );
}
